// What each program passes to the context menu once (like configureRimoPreview): look, toast, clipboard, translated
// labels. The core never imports a program, Tauri or an i18n module – everything program-specific comes in here.

/** Look of the menu. Values are CSS strings, so fixed colours and design tokens (`var(--rc-…)`) both work. */
export interface CtxTheme {
  bg: string; line: string; sep: string; hover: string;
  fg: string; dim: string; mute: string; danger: string;
  radius: string | number; rowRadius: string | number; rowHeight: number; iconSize: number;
  shadow: string; font: string;
  /** Group headers; `headerUpper` = uppercase with letter spacing (RimoCompta), off = plain (RimoID). */
  headerFont: string; headerUpper: boolean;
  hintFont: string;
  minWidth: number;
  /** Short scale-in when opening. */
  animate: boolean;
  /** false = never an icon column, even when entries carry icons (RimoID). */
  icons: boolean;
  /** Panel padding and the gap between rows. */
  padding: string | number; gap: number;
  /** Margin of a separator line (CSS, e.g. „4px 6px“ or RimoID's „4px 0“). */
  sepInset: string;
  /** Group headers: colour (default `mute`), a hairline under it (`rule`), padding and space below. */
  header: CtxHeaderStyle;
  /** Text colour of a row that is not highlighted (RimoPortail: fg-muted until hover); default `fg`. */
  rowFg?: string;
  /** Background of a highlighted danger entry (RimoPortail: its own red tint); default `hover`. */
  dangerHover?: string;
  /** Stroke width of the line icons (RimoPortail 1.7). */
  iconStroke: number;
}

export interface CtxHeaderStyle { color?: string; rule?: boolean; padding?: string; marginBottom?: number }

/** Labels of the text entries, already in the UI language. */
export interface CtxLabels {
  cut: string; copy: string; paste: string; selectAll: string;
  /** Toast after copying a value, e.g. „Betrag kopiert: 1805,75“ (`shown` is already shortened). */
  copied: (what: string, shown: string) => string;
  copyFailed: string;
  /** Toast when reading the clipboard is not allowed, e.g. „Einfügen mit ⌘V“. */
  pasteWith: (keys: string) => string;
}

export interface CtxClipboard {
  write(text: string): Promise<boolean>;
  read(): Promise<string | null>;
}

export type CtxToast = (text: string, kind: 'success' | 'warning') => void;

export interface CtxConfig {
  /** The program's own toast (required: a forgotten hook-up shows up in the type check). */
  toast: CtxToast;
  /** Fixed labels, or a function when the program switches language at runtime (read at every right-click). */
  labels: CtxLabels | (() => CtxLabels);
  /** Omitted keys keep the default; `header` is merged key by key. */
  theme?: Partial<Omit<CtxTheme, 'header'>> & { header?: CtxHeaderStyle };
  /** Default: the web clipboard with a hidden-textarea fallback. Desktop programs pass their Tauri version. */
  clipboard?: CtxClipboard;
  /** true → the menu carries `data-no-tr` / `translate="no"` (word-pair translations leave it alone). Default false. */
  noTranslate?: boolean;
  /** Above RimoPreview (20000 in every program today). */
  zIndex?: number;
}

/** RimoCompta's look until 26.10.2026 – the default for keys a program leaves out. */
export const DEFAULT_THEME: CtxTheme = {
  bg: '#2A2627', line: 'rgba(255,255,255,.14)', sep: 'rgba(255,255,255,.08)', hover: '#3A3536',
  fg: '#FBFAFA', dim: '#9B9698', mute: '#8C8789', danger: '#E57373',
  radius: 6, rowRadius: 4, rowHeight: 30, iconSize: 14,
  shadow: '0 12px 28px rgba(0,0,0,.45)', font: '500 13px/1 Inter,sans-serif',
  headerFont: '700 10px/1.2 Barlow,sans-serif', headerUpper: true,
  hintFont: "500 11.5px/1 'JetBrains Mono',monospace",
  minWidth: 224, animate: true,
  icons: true, padding: 4, gap: 1, sepInset: '4px 6px',
  header: { rule: false, padding: '8px 10px 6px', marginBottom: 0 },
  iconStroke: 2,
};

/** Web clipboard: async API, else a hidden textarea + copy command (works inside a click on older WebViews). */
export const webClipboard: CtxClipboard = {
  async write(text) {
    try { await navigator.clipboard.writeText(text); return true; } catch { /* fallback below */ }
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  },
  async read() {
    try { return await navigator.clipboard.readText(); } catch { return null; }
  },
};

interface Resolved { toast: CtxToast; readonly labels: CtxLabels; theme: CtxTheme; clipboard: CtxClipboard; noTranslate: boolean; zIndex: number }
let cfg: Resolved | null = null;

export function configureContextMenu(c: CtxConfig): void {
  const labels = c.labels;
  cfg = { toast: c.toast, get labels() { return typeof labels === 'function' ? labels() : labels; }, theme: { ...DEFAULT_THEME, ...c.theme, header: { ...DEFAULT_THEME.header, ...c.theme?.header } }, clipboard: c.clipboard ?? webClipboard, noTranslate: !!c.noTranslate, zIndex: c.zIndex ?? 20000 };
}

/** The configuration; throws when the program forgot `configureContextMenu` (shows up at the first right-click). */
export function ctxConfig(): Resolved {
  if (!cfg) throw new Error('configureContextMenu() was not called');
  return cfg;
}

/** Toast through the program's own channel. */
export const ctxToast = (text: string, kind: 'success' | 'warning' = 'success') => ctxConfig().toast(text, kind);
